import { Injectable, inject, signal } from '@angular/core';
import { SwPush } from '@angular/service-worker';
import { HttpClientWrapperService } from '../http/http-client-wrapper.service';
import { ApiUrlService } from '../api/api-url.service';
import { LoggerService } from '../logger/logger.service';
import { NavController } from '@ionic/angular/standalone';
import { environment } from 'src/environments/environment';
import { EMPTY, firstValueFrom, Observable, take } from 'rxjs';

export type PushOwnerType = 'USER' | 'CHARITY' | 'BUSINESS';

/**
 * Detected push state for a specific owner profile on this browser:
 * - unsupported: push not available here (non-production build, missing VAPID key, no Push API)
 * - install-required: iOS browser without the PWA installed (Web Push needs Add to Home Screen)
 * - blocked: the user denied notification permission in browser settings
 * - off: no backend subscription row for this (browser, owner) pair
 * - on: backend subscription row exists for this (browser, owner) pair
 */
export type PushStatus = 'unsupported' | 'install-required' | 'blocked' | 'off' | 'on';

export interface PushTestResult {
  sent: number;
  filtered: number;
  pruned: number;
  failed: number;
}

interface PushSubscriptionRequest {
  endpoint: string;
  p256dh: string;
  auth: string;
  type: PushOwnerType;
  userId?: number;
  charityId?: number;
  businessId?: number;
}

interface PushOwnerFields {
  userId?: number;
  charityId?: number;
  businessId?: number;
}

interface UnsubscribeRequest extends PushOwnerFields {
  endpoint: string;
  type?: PushOwnerType;
}

interface UnsubscribeResponse {
  deleted: boolean;
}

interface PushStatusRequest extends PushOwnerFields {
  endpoint: string;
  type: PushOwnerType;
}

interface PushStatusResponse {
  subscribed: boolean;
}

interface TestPushRequest extends PushOwnerFields {
  type: PushOwnerType;
}

// Message type for foreground notification clicks posted from src/sw-custom.js.
// Distinct from Angular SwPush's 'NOTIFICATION_CLICK' to avoid the SwPush listener
// (when active) consuming a payload it can't deserialize.
const CUSTOM_NOTIFICATION_CLICK: string = 'CUSTOM_NOTIFICATION_CLICK';

@Injectable({ providedIn: 'root' })
export class PushNotificationService {
  // SwPush is only provided when the Angular Service Worker is registered
  // (production builds via provideServiceWorker in main.ts). Make it optional
  // so this service can still be constructed in tests / non-PWA contexts.
  private readonly swPush: SwPush | null = inject(SwPush, { optional: true });
  private readonly httpClient = inject(HttpClientWrapperService);
  private readonly apiUrlService = inject(ApiUrlService);
  private readonly logger = inject(LoggerService);
  private readonly navController = inject(NavController);

  private readonly _isSubscribed = signal<boolean>(false);
  private readonly _subscriptionError = signal<string | null>(null);
  private readonly _currentEndpoint = signal<string | null>(null);

  readonly isSubscribed = this._isSubscribed.asReadonly();
  readonly subscriptionError = this._subscriptionError.asReadonly();

  isAvailable(): boolean {
    return !!this.swPush?.isEnabled && !!environment.pushNotificationVapidPublicKey;
  }

  // The in-memory `_isSubscribed` signal resets to `false` on every page load,
  // but the browser may still hold an active PushSubscription from a prior
  // session. Query SwPush directly so returning users see the correct toggle
  // state and category preferences load.
  async syncSubscriptionState(): Promise<boolean> {
    if (!this.swPush?.isEnabled) {
      return false;
    }
    try {
      const liveSub: PushSubscription | null = await this.getLiveSubscription();
      if (liveSub) {
        this._currentEndpoint.set(liveSub.endpoint);
        this._isSubscribed.set(true);
        return true;
      }
      this._currentEndpoint.set(null);
      this._isSubscribed.set(false);
      return false;
    } catch (error) {
      this.logger.warn('Failed to sync push subscription state', { error });
      return this._isSubscribed();
    }
  }

  constructor() {
    this.initializeNotificationListeners();
  }

  /**
   * Detects the push state for one owner profile on this browser. 'on'/'off'
   * reflect the backend subscription row for the (endpoint, owner) pair — not
   * just the browser-level subscription, which is shared by all owners.
   */
  async getStatus(type: PushOwnerType, id: number): Promise<PushStatus> {
    if (!this.isAvailable() || !('Notification' in window) || !('PushManager' in window)) {
      return this.isIosBrowserWithoutInstalledPwa() ? 'install-required' : 'unsupported';
    }

    if (Notification.permission === 'denied') {
      return 'blocked';
    }

    const endpoint: string | null = await this.getLiveEndpoint();
    if (!endpoint) {
      return 'off';
    }

    try {
      const request: PushStatusRequest = {
        endpoint,
        type,
        ...this.ownerFields(type, id),
      };
      const response: PushStatusResponse | undefined = await firstValueFrom(
        this.httpClient.post<PushStatusResponse, PushStatusRequest>(
          this.apiUrlService.buildUrl('/api/v1/push-subscriptions/status'),
          request
        )
      );
      return response?.subscribed ? 'on' : 'off';
    } catch (error) {
      // Fail towards 'off': the Enable action is idempotent, so offering it
      // again is always safe — hiding a broken state is not.
      this.logger.warn('Failed to check push subscription status', { error, type });
      return 'off';
    }
  }

  async requestPermissionAndSubscribe(
    type: PushOwnerType,
    id: number
  ): Promise<void> {
    // Runtime validation
    if (!id || id <= 0) {
      const error: string = 'Invalid ID provided for push subscription';
      this.logger.error(error, undefined, { type });
      throw new Error(error);
    }

    // Push requires a registered service worker (sw-custom.js) — enabled on dev/local as well
    // as prod via enableServiceWorker (SUN-725), not just production builds.
    if (!this.swPush || !this.swPush.isEnabled) {
      this.logger.warn('Push unavailable: service worker not registered', { type });
      throw new Error('Push notifications require a registered service worker');
    }

    // Check VAPID key configuration
    if (!environment.pushNotificationVapidPublicKey) {
      this.logger.error('VAPID public key not configured', undefined, { type });
      throw new Error('Push notifications not configured');
    }

    // One physical browser subscription is shared by every owner (USER +
    // each CHARITY/BUSINESS). Record whether it already existed so a failed
    // registration for THIS owner never tears down other owners' delivery.
    let hadExistingSubscription: boolean = false;
    try {
      hadExistingSubscription = (await this.getLiveSubscription()) !== null;
    } catch {
      hadExistingSubscription = false;
    }

    let subscription: PushSubscription | null = null;
    let backendRegistered: boolean = false;
    try {
      // Request subscription from browser (returns the existing one if present)
      subscription = await this.swPush.requestSubscription({
        serverPublicKey: environment.pushNotificationVapidPublicKey,
      });

      // Extract keys from subscription. PushSubscriptionJSON types `keys` as
      // a Record under the hood, but the DOM lib narrows it to `JsonWebKey`
      // which doesn't expose p256dh/auth — cast to the actual shape.
      const keys: { p256dh?: string; auth?: string } | undefined =
        subscription.toJSON().keys as { p256dh?: string; auth?: string } | undefined;
      if (!keys?.p256dh || !keys?.auth) {
        throw new Error('Invalid subscription keys');
      }

      // Build request payload
      const request: PushSubscriptionRequest = {
        endpoint: subscription.endpoint,
        p256dh: keys.p256dh,
        auth: keys.auth,
        type,
        ...this.ownerFields(type, id),
      };

      // Register with backend
      await firstValueFrom(
        this.httpClient.post<void, PushSubscriptionRequest>(
          this.apiUrlService.buildUrl('/api/v1/push-subscriptions/subscribe'),
          request
        )
      );
      backendRegistered = true;

      // Update state
      this._currentEndpoint.set(subscription.endpoint);
      this._isSubscribed.set(true);
      this._subscriptionError.set(null);
      this.logger.info('Push subscription successful', { type });
    } catch (error) {
      // Roll back the browser subscription only when THIS attempt created it.
      // If it pre-existed, other owners (USER / other orgs) are still using
      // it — unsubscribing the browser would silently kill their delivery.
      if (subscription && !backendRegistered && !hadExistingSubscription) {
        try {
          await this.swPush.unsubscribe();
        } catch (rollbackError) {
          this.logger.error(
            'Failed to roll back browser push subscription after API failure',
            rollbackError,
            { type }
          );
        }
      }
      const isPermissionDenied: boolean = error instanceof Error && error.name === 'NotAllowedError';
      this._subscriptionError.set(
        isPermissionDenied ? 'Notification permission denied' : 'Failed to enable notifications'
      );
      if (isPermissionDenied) {
        this.logger.warn('Push subscription denied by user', { type });
      } else {
        this.logger.error('Push subscription failed', error, { type });
      }
      throw error;
    }
  }

  /**
   * Removes the backend subscription row for one owner only. Never touches
   * the physical browser subscription — that is shared by all owners on this
   * device and is only torn down by unsubscribeAll() (logout).
   */
  async unsubscribeOwner(type: PushOwnerType, id: number): Promise<boolean> {
    const endpoint: string | null = await this.getLiveEndpoint();
    if (!endpoint) {
      this.logger.warn('No active push subscription for owner unsubscribe', { type });
      return true;
    }

    try {
      const request: UnsubscribeRequest = {
        endpoint,
        type,
        ...this.ownerFields(type, id),
      };
      await firstValueFrom(
        this.httpClient.post<UnsubscribeResponse, UnsubscribeRequest>(
          this.apiUrlService.buildUrl('/api/v1/push-subscriptions/unsubscribe'),
          request
        )
      );
      this.logger.info('Push owner unsubscribe successful', { type });
      return true;
    } catch (error) {
      this.logger.error('Push owner unsubscribe failed', error, { type });
      return false;
    }
  }

  /**
   * Logout cleanup: removes every backend row the current user owns for this
   * endpoint, then unsubscribes the physical browser subscription.
   */
  async unsubscribeAll(): Promise<boolean> {
    let endpoint: string | null = this._currentEndpoint();

    // After a page refresh / cold start the in-memory endpoint is empty even
    // when the browser still holds an active PushSubscription. Query SwPush
    // for the live subscription so logout tears down both backend + browser.
    if (!endpoint) {
      endpoint = await this.getLiveEndpoint();
    }

    if (!endpoint) {
      this.logger.warn('No active push subscription to unsubscribe');
      // Nothing to remove — treat as success so callers don't show a stale
      // "still subscribed" UI when there's no subscription to begin with.
      this._currentEndpoint.set(null);
      this._isSubscribed.set(false);
      return true;
    }

    try {
      // Call backend to remove subscription rows (no owner fields = all owned rows)
      await firstValueFrom(
        this.httpClient.post<UnsubscribeResponse, UnsubscribeRequest>(
          this.apiUrlService.buildUrl('/api/v1/push-subscriptions/unsubscribe'),
          { endpoint }
        )
      );

      // Backend record is gone — clear in-memory state now so it can't
      // drift from server truth if the browser unsubscribe below fails.
      this._currentEndpoint.set(null);
      this._isSubscribed.set(false);

      // Unsubscribe from browser (if SwPush is available)
      if (this.swPush?.isEnabled) {
        try {
          await this.swPush.unsubscribe();
        } catch (browserError) {
          this.logger.error('Browser push unsubscribe failed (state already cleared)', browserError);
        }
      }

      this.logger.info('Push unsubscribe successful');
      return true;
    } catch (error) {
      // Log but don't throw - unsubscribe should not block logout.
      // Return false so callers (e.g. settings page) can keep the UI in sync
      // with the still-active subscription instead of silently showing "off".
      this.logger.error('Push unsubscribe failed (non-blocking)', error);
      return false;
    }
  }

  /** Sends a real test push to all of this owner's subscribed browsers. */
  async sendTestPush(type: PushOwnerType, id: number): Promise<PushTestResult> {
    const request: TestPushRequest = {
      type,
      ...this.ownerFields(type, id),
    };
    const result: PushTestResult | undefined = await firstValueFrom(
      this.httpClient.post<PushTestResult, TestPushRequest>(
        this.apiUrlService.buildUrl('/api/v1/push-subscriptions/test'),
        request
      )
    );
    return result ?? { sent: 0, filtered: 0, pruned: 0, failed: 0 };
  }

  listenToNotifications(): Observable<unknown> {
    return this.swPush?.messages ?? EMPTY;
  }

  private ownerFields(type: PushOwnerType, id: number): PushOwnerFields {
    return {
      ...(type === 'USER' && { userId: id }),
      ...(type === 'CHARITY' && { charityId: id }),
      ...(type === 'BUSINESS' && { businessId: id }),
    };
  }

  private async getLiveSubscription(): Promise<PushSubscription | null> {
    if (!this.swPush?.isEnabled) {
      return null;
    }
    return firstValueFrom(this.swPush.subscription.pipe(take(1)));
  }

  private async getLiveEndpoint(): Promise<string | null> {
    try {
      const liveSub: PushSubscription | null = await this.getLiveSubscription();
      return liveSub?.endpoint ?? null;
    } catch (error) {
      this.logger.info('Failed to read current push subscription', { error });
      return null;
    }
  }

  private isIosBrowserWithoutInstalledPwa(): boolean {
    const isIos: boolean = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const standaloneNavigator: Navigator & { standalone?: boolean } = navigator;
    const isInstalled: boolean =
      window.matchMedia('(display-mode: standalone)').matches ||
      standaloneNavigator.standalone === true;
    return isIos && !isInstalled;
  }

  private getTargetUrl(url: string | undefined): string {
    // Backend URLs carry org ids as query params (e.g. ?charityId=51), and the
    // orgContextGuard switches context on navigation — org notifications can
    // deep link just like user ones. Only same-origin relative paths allowed.
    if (url && url.startsWith('/') && !url.startsWith('//')) {
      return url;
    }

    // Fallback to main page
    return '/main';
  }

  private initializeNotificationListeners(): void {
    // Foreground clicks: sw-custom.js focuses an existing tab and posts a CUSTOM_NOTIFICATION_CLICK
    // message; this listener navigates that tab. Background clicks (app closed) don't need a
    // listener — sw-custom.js calls clients.openWindow(targetUrl) directly.
    if (!('serviceWorker' in navigator)) {
      return;
    }
    navigator.serviceWorker.ready
      .then(() => {
        navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
          if (event.data?.type !== CUSTOM_NOTIFICATION_CLICK) {
            return;
          }
          const notificationType: string | undefined = event.data.notificationType;
          const url: string | undefined = event.data.url;
          const targetUrl: string = this.getTargetUrl(url);

          this.logger.info('Navigating from service worker notification click', {
            notificationType, url, targetUrl,
          });
          this.navController.navigateForward(targetUrl);
        });
      })
      .catch((error: unknown) => {
        this.logger.error('Failed to register service worker message listener', error);
      });
  }
}
